// Conservative, rule-based English rewriter that runs fully in the browser.
//
// It does NOT swap words from a general thesaurus (that produced broken English such as
// "obtain paid rapider"). Instead it only applies hand-checked swaps where every inflected form is
// listed explicitly, so the grammar of the sentence is preserved:
//   light  -> whole-phrase swaps ("in order to" -> "to", "I think" -> "I believe")
//   medium -> + a short list of safe single words (big -> large, buy -> purchase ...)
//   strong -> + contractions expanded/contracted and simple sentence restructuring
// A language model would paraphrase far better; this is the honest no-API alternative.

export type Strength = "light" | "medium" | "strong";
export interface Segment { text: string; original?: string }

const S = "", E = "";

type Rule = [RegExp, string[]];
const r = (src: string, alts: string[]): Rule => [new RegExp(`\\b${src}\\b`, "gi"), alts];

const PHRASES: Rule[] = [
  r("in order to", ["to"]),
  r("due to the fact that", ["because"]),
  r("in spite of", ["despite"]),
  r("as well as", ["along with"]),
  r("a number of", ["several"]),
  r("a lot of", ["plenty of"]),
  r("a few", ["a handful of"]),
  r("at this point in time", ["now"]),
  r("at the present time", ["currently"]),
  r("in the event that", ["if"]),
  r("prior to", ["before"]),
  r("subsequent to", ["after"]),
  r("is able to", ["can"]),
  r("are able to", ["can"]),
  r("right away", ["immediately"]),
  r("right now", ["at the moment"]),
  r("for example", ["for instance"]),
  r("because of", ["due to"]),
  r("i think", ["I believe"]),
  r("i want to", ["I would like to"]),
  r("let me know", ["please tell me", "feel free to tell me"]),
  r("thank you very much for", ["thank you so much for", "many thanks for"]),
  r("thanks a lot for", ["many thanks for"]),
  r("don't forget to", ["remember to"]),
  r("don't forget", ["remember"]),
  r("get started", ["begin"]),
  r("quick update", ["brief update"]),
  r("quick question", ["short question"]),
  r("quick look", ["brief look"]),
  r("as soon as possible", ["at your earliest convenience"]),
];

const WORDS: Rule[] = [
  r("big", ["large"]),
  r("huge", ["enormous"]),
  r("important", ["significant"]),
  r("main", ["primary"]),
  r("easy", ["straightforward"]),
  r("buy", ["purchase"]), r("buys", ["purchases"]), r("bought", ["purchased"]), r("buying", ["purchasing"]),
  r("goals", ["aims", "objectives"]), r("goal", ["aim", "objective"]),
  r("companies", ["businesses"]), r("company", ["business"]),
  r("problems", ["issues"]), r("problem", ["issue"]),
  r("ideas", ["concepts"]), r("idea", ["concept"]),
  r("methods", ["approaches"]), r("method", ["approach"]),
  r("(will|to|would|should|can|must|may) start", ["$1 begin"]),
  r("(will|to|would|should|can|must|may) help", ["$1 assist"]),
  r("(needs|need) to", ["$1 to"]), // placeholder kept identical, filtered below
  r("however", ["that said"]),
  r("also", ["as well"]),
];
// "need to" rule above intentionally maps to itself; remove no-ops so they never count as a change.
const WORD_RULES = WORDS.filter(([, a]) => a.some(x => x !== "$1 to"));

const EXPAND: [RegExp, string][] = [
  [/\bcan't\b/gi, "cannot"], [/\bwon't\b/gi, "will not"], [/\bdon't\b/gi, "do not"], [/\bdoesn't\b/gi, "does not"],
  [/\bdidn't\b/gi, "did not"], [/\bisn't\b/gi, "is not"], [/\baren't\b/gi, "are not"], [/\bwasn't\b/gi, "was not"],
  [/\bcouldn't\b/gi, "could not"], [/\bshouldn't\b/gi, "should not"], [/\bwouldn't\b/gi, "would not"],
  [/\bhaven't\b/gi, "have not"], [/\bhasn't\b/gi, "has not"],
  [/\bI'm\b/g, "I am"], [/\b(you|we|they)'re\b/gi, "$1 are"], [/\b(I|you|we|they)'ll\b/gi, "$1 will"],
  [/\b(I|you|we|they)'ve\b/gi, "$1 have"],
];
const CONTRACT: [RegExp, string][] = [
  [/\bdo not\b/gi, "don't"], [/\bdoes not\b/gi, "doesn't"], [/\bdid not\b/gi, "didn't"], [/\bis not\b/gi, "isn't"],
  [/\bare not\b/gi, "aren't"], [/\bcannot\b/gi, "can't"], [/\bwill not\b/gi, "won't"], [/\bI am\b/g, "I'm"],
  [/\b(you|we|they) are\b/gi, "$1're"], [/\b(I|you|we|they) will\b/gi, "$1'll"],
];

const CONNECTORS: [RegExp, string[]][] = [
  [/^But\b/, ["However,", "Even so,"]],
  [/^Also,/, ["In addition,"]],
  [/^So\b,?/, ["As a result,"]],
];

function caseLike(src: string, rep: string): string {
  if (src.length > 1 && src === src.toUpperCase() && /[A-Z]/.test(src)) return rep.toUpperCase();
  if (/^[A-Z]/.test(src)) return rep.charAt(0).toUpperCase() + rep.slice(1);
  return rep;
}

function applyRules(text: string, rules: Rule[], pick: (n: number) => number): string {
  let out = text;
  for (const [re, alts] of rules) {
    out = out.replace(new RegExp(re.source, re.flags), (...m) => {
      const matched = m[0] as string;
      if (matched.includes(S) || matched.includes(E)) return matched;
      const groups = m.slice(1, -2) as string[];
      const alt = alts[pick(alts.length)];
      let rep = alt.replace(/\$(\d)/g, (_, d) => groups[Number(d) - 1] ?? "");
      if (rep === matched) return matched;
      rep = caseLike(matched, rep);
      return `${S}${rep}${E}${""}${matched}${""}`;
    });
  }
  return out;
}

function reorderBecause(sentence: string): string | null {
  const m = /^([^,;:]+?) because ([^,;:]+?)([.!?])$/.exec(sentence.trim());
  if (!m) return null;
  const [, main, reason, end] = m;
  if (main.split(/\s+/).length < 2 || /^(and|but|or)\b/i.test(main)) return null;
  const lowerFirst = (s: string) => (/^[A-Z][a-z]/.test(s) ? s[0].toLowerCase() + s.slice(1) : s);
  return `Because ${reason}, ${lowerFirst(main)}${end}`;
}

function splitSentences(line: string): string[] {
  return line.match(/[^.!?]+[.!?]+["')\]]*\s*|[^.!?]+$/g) ?? [line];
}

/** Returns the rewritten text as segments so the UI can highlight (and revert) changed words. */
export function paraphraseSegments(text: string, strength: Strength = "medium", seed = 0): Segment[] {
  let n = seed;
  const pick = (len: number) => (len <= 1 ? 0 : Math.abs(n++) % len);
  const hasContractions = /\b\w+n't\b|\bI'm\b/.test(text);

  const lines = text.split("\n").map(line => {
    // Only touch English/Latin text; other scripts pass through untouched.
    if (!/[A-Za-z]/.test(line)) return line;
    return splitSentences(line).map(sentence => {
      const trail = /\s*$/.exec(sentence)![0];
      let s = sentence.slice(0, sentence.length - trail.length);
      if (strength === "strong") {
        const re = reorderBecause(s);
        if (re && pick(2) === 0 && !/[-]/.test(s)) {
          return `${S}${re}${E}${s}${trail}`;
        }
        for (const [cre, alts] of CONNECTORS) {
          s = s.replace(cre, mt => `${S}${alts[pick(alts.length)]}${E}${mt}`);
        }
      }
      s = applyRules(s, PHRASES, pick);
      if (strength !== "light") s = applyRules(s, WORD_RULES, pick);
      if (strength === "strong") s = applyRules(s, (hasContractions ? EXPAND : CONTRACT).map(([re, t]) => [re, [t]] as Rule), pick);
      return s + trail;
    }).join("");
  });

  const joined = lines.join("\n");
  const segs: Segment[] = [];
  const re = /([^]*)([^]*)/g;
  let last = 0, mm: RegExpExecArray | null;
  while ((mm = re.exec(joined))) {
    if (mm.index > last) segs.push({ text: joined.slice(last, mm.index) });
    segs.push({ text: mm[1], original: mm[2] });
    last = mm.index + mm[0].length;
  }
  if (last < joined.length) segs.push({ text: joined.slice(last) });
  return segs;
}

export function paraphrase(text: string, strength: Strength = "medium", seed = 0): string {
  return paraphraseSegments(text, strength, seed).map(s => s.text).join("");
}
